console.log('[table-modules.ts loaded]');

/**
 * Airtable Table-Specific Modules
 * 
 * Dedicated functions for each Airtable table type
 * Provides type-safe operations for CROs, Staining Platforms, etc.
 * Builds on top of the generic REST API client
 */

import {
  fetchRecords,
  createRecord,
  updateRecord,
  deleteRecord,
  CRORecord,
  StainingPlatformRecord,
  ImageScanningRecord,
  ImageAnalysisRecord,
  CompanyRecord,
  AirtableRecord,
  QueryOptions,
} from './';

/**
 * CRO Table Module (requires tableId for all functions)
 */
export const CROModule = {
  /**
   * Fetch all CRO records
   * @param tableId Airtable table ID (required)
   */
  async getAll(tableId: string, options?: QueryOptions): Promise<CRORecord[]> {
    const response = await fetchRecords(tableId, options);
    return response.records as CRORecord[];
  },

  /**
   * Fetch a single CRO record by ID
   * @param tableId Airtable table ID (required)
   */
  async getById(tableId: string, id: string): Promise<CRORecord | null> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `RECORD_ID() = '${id}'`,
      maxRecords: 1,
    });
    if (response.records.length === 0) {
      return null;
    }
    return response.records[0] as CRORecord;
  },

  /**
   * Fetch CRO records by name (partial match)
   * @param tableId Airtable table ID (required)
   */
  async getByName(tableId: string, name: string, options?: Omit<QueryOptions, 'filterByFormula'>): Promise<CRORecord[]> {
    const response = await fetchRecords(tableId, {
      ...options,
      filterByFormula: `FIND("${name.replace(/"/g, '\\"')}", {Name}) > 0`,
    });
    return response.records as CRORecord[];
  },

  /**
   * Get CROs by staining platform
   * @param tableId Airtable table ID (required)
   */
  async getByStainingPlatform(tableId: string, platformId: string): Promise<CRORecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${platformId}", ARRAYJOIN({StainingPlatforms}, ",")) > 0`,
    });
    return response.records as CRORecord[];
  },

  /**
   * Get CROs by image scanning method
   * @param tableId Airtable table ID (required)
   */
  async getByImageScanning(tableId: string, imageScanningId: string): Promise<CRORecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${imageScanningId}", ARRAYJOIN({ImageScanning}, ",")) > 0`,
    });
    return response.records as CRORecord[];
  },

  /**
   * Get CROs by image analysis method
   * @param tableId Airtable table ID (required)
   */
  async getByImageAnalysis(tableId: string, imageAnalysisId: string): Promise<CRORecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${imageAnalysisId}", ARRAYJOIN({ImageAnalysis}, ",")) > 0`,
    });
    return response.records as CRORecord[];
  },

  /**
   * Create a new CRO record
   * @param tableId Airtable table ID (required)
   */
  async create(tableId: string, fields: Omit<CRORecord['fields'], 'id'>): Promise<CRORecord> {
    return createRecord(tableId, fields) as Promise<CRORecord>;
  },

  /**
   * Update an existing CRO record
   * @param tableId Airtable table ID (required)
   */
  async update(tableId: string, id: string, fields: Partial<CRORecord['fields']>): Promise<CRORecord> {
    return updateRecord(tableId, id, fields) as Promise<CRORecord>;
  },

  /**
   * Delete a CRO record
   * @param tableId Airtable table ID (required)
   */
  async delete(tableId: string, id: string): Promise<{ id: string; deleted: boolean }> {
    return deleteRecord(tableId, id);
  },
};

/**
 * Staining Platform Table Module
 */
export const StainingPlatformModule = {
  /**
   * Fetch all Staining Platform records
   */
  async getAll(tableId: string, options?: QueryOptions): Promise<StainingPlatformRecord[]> {
    const response = await fetchRecords(tableId, options);
    return response.records as StainingPlatformRecord[];
  },

  /**
   * Fetch a single Staining Platform record by ID
   */
  async getById(tableId: string, id: string): Promise<StainingPlatformRecord | null> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `RECORD_ID() = '${id}'`,
      maxRecords: 1,
    });
    
    if (response.records.length === 0) {
      return null;
    }
    
    return response.records[0] as StainingPlatformRecord;
  },

  /**
   * Fetch Staining Platform records by manufacturer
   */
  async getByManufacturer(tableId: string, manufacturer: string, options?: Omit<QueryOptions, 'filterByFormula'>): Promise<StainingPlatformRecord[]> {
    const response = await fetchRecords(tableId, {
      ...options,
      filterByFormula: `FIND("${manufacturer.replace(/"/g, '\\"')}", {Manufacturer}) > 0`,
    });
    
    return response.records as StainingPlatformRecord[];
  },

  /**
   * Get staining platforms by company
   */
  async getByCompany(tableId: string, companyId: string): Promise<StainingPlatformRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${companyId}", ARRAYJOIN({Companies}, ",")) > 0`,
    });
    return response.records as StainingPlatformRecord[];
  },

  /**
   * Get staining platforms that support a specific sample type
   */
  async getBySampleType(tableId: string, sampleType: string): Promise<StainingPlatformRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${sampleType.replace(/"/g, '\\"')}", ARRAYJOIN({SampleTypes}, ",")) > 0`,
    });
    return response.records as StainingPlatformRecord[];
  },

  /**
   * Create a new Staining Platform record
   */
  async create(tableId: string, fields: Omit<StainingPlatformRecord['fields'], 'id'>): Promise<StainingPlatformRecord> {
    return createRecord(tableId, fields) as Promise<StainingPlatformRecord>;
  },

  /**
   * Update an existing Staining Platform record
   */
  async update(tableId: string, id: string, fields: Partial<StainingPlatformRecord['fields']>): Promise<StainingPlatformRecord> {
    return updateRecord(tableId, id, fields) as Promise<StainingPlatformRecord>;
  },

  /**
   * Delete a Staining Platform record
   */
  async delete(tableId: string, id: string): Promise<{ id: string; deleted: boolean }> {
    return deleteRecord(tableId, id);
  },
};

/**
 * Image Scanning Table Module
 */
export const ImageScanningModule = {
  /**
   * Fetch all Image Scanning records
   */
  async getAll(tableId: string, options?: QueryOptions): Promise<ImageScanningRecord[]> {
    const response = await fetchRecords(tableId, options);
    return response.records as ImageScanningRecord[];
  },

  /**
   * Fetch a single Image Scanning record by ID
   */
  async getById(tableId: string, id: string): Promise<ImageScanningRecord | null> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `RECORD_ID() = '${id}'`,
      maxRecords: 1,
    });
    
    if (response.records.length === 0) {
      return null;
    }
    
    return response.records[0] as ImageScanningRecord;
  },

  /**
   * Fetch Image Scanning records by technology
   */
  async getByTechnology(tableId: string, technology: string, options?: Omit<QueryOptions, 'filterByFormula'>): Promise<ImageScanningRecord[]> {
    const response = await fetchRecords(tableId, {
      ...options,
      filterByFormula: `FIND("${technology.replace(/"/g, '\\"')}", {Technology}) > 0`,
    });
    
    return response.records as ImageScanningRecord[];
  },

  /**
   * Get image scanning options by company
   */
  async getByCompany(tableId: string, companyId: string): Promise<ImageScanningRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${companyId}", ARRAYJOIN({Companies}, ",")) > 0`,
    });
    return response.records as ImageScanningRecord[];
  },

  /**
   * Get image scanning options by supported slide type
   */
  async getBySupportedSlide(tableId: string, slideType: string): Promise<ImageScanningRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${slideType.replace(/"/g, '\\"')}", ARRAYJOIN({SupportedSlides}, ",")) > 0`,
    });
    return response.records as ImageScanningRecord[];
  },

  /**
   * Create a new Image Scanning record
   */
  async create(tableId: string, fields: Omit<ImageScanningRecord['fields'], 'id'>): Promise<ImageScanningRecord> {
    return createRecord(tableId, fields) as Promise<ImageScanningRecord>;
  },

  /**
   * Update an existing Image Scanning record
   */
  async update(tableId: string, id: string, fields: Partial<ImageScanningRecord['fields']>): Promise<ImageScanningRecord> {
    return updateRecord(tableId, id, fields) as Promise<ImageScanningRecord>;
  },

  /**
   * Delete an Image Scanning record
   */
  async delete(tableId: string, id: string): Promise<{ id: string; deleted: boolean }> {
    return deleteRecord(tableId, id);
  },
};

/**
 * Image Analysis Table Module
 */
export const ImageAnalysisModule = {
  /**
   * Fetch all Image Analysis records
   */
  async getAll(tableId: string, options?: QueryOptions): Promise<ImageAnalysisRecord[]> {
    const response = await fetchRecords(tableId, options);
    return response.records as ImageAnalysisRecord[];
  },

  /**
   * Fetch a single Image Analysis record by ID
   */
  async getById(tableId: string, id: string): Promise<ImageAnalysisRecord | null> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `RECORD_ID() = '${id}'`,
      maxRecords: 1,
    });
    if (!response || !response.records) {
      console.error("[table-modules.ts] fetchRecords returned undefined or missing records!", { tableId, id, response });
      throw new Error("Table module error: fetchRecords returned undefined or missing records");
    }
    if (response.records.length === 0) {
      return null;
    }
    return response.records[0] as ImageAnalysisRecord;
  },

  /**
   * Fetch Image Analysis records by analysis type
   */
  async getByAnalysisType(tableId: string, analysisType: string, options?: Omit<QueryOptions, 'filterByFormula'>): Promise<ImageAnalysisRecord[]> {
    const response = await fetchRecords(tableId, {
      ...options,
      filterByFormula: `FIND("${analysisType.replace(/"/g, '\\"')}", {AnalysisType}) > 0`,
    });
    
    return response.records as ImageAnalysisRecord[];
  },

  /**
   * Get image analysis tools by company
   */
  async getByCompany(tableId: string, companyId: string): Promise<ImageAnalysisRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${companyId}", ARRAYJOIN({Companies}, ",")) > 0`,
    });
    return response.records as ImageAnalysisRecord[];
  },

  /**
   * Get image analysis tools by supported file format
   */
  async getBySupportedFormat(tableId: string, format: string): Promise<ImageAnalysisRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${format.replace(/"/g, '\\"')}", ARRAYJOIN({SupportedFormats}, ",")) > 0`,
    });
    return response.records as ImageAnalysisRecord[];
  },

  /**
   * Get cloud-based analysis tools only
   */
  async getCloudBased(tableId: string): Promise<ImageAnalysisRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: '{CloudBased} = TRUE()',
    });
    return response.records as ImageAnalysisRecord[];
  },

  /**
   * Get machine learning capable analysis tools only
   */
  async getMachineLearningCapable(tableId: string): Promise<ImageAnalysisRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: '{MachineLearning} = TRUE()',
    });
    return response.records as ImageAnalysisRecord[];
  },

  /**
   * Create a new Image Analysis record
   */
  async create(tableId: string, fields: Omit<ImageAnalysisRecord['fields'], 'id'>): Promise<ImageAnalysisRecord> {
    return createRecord(tableId, fields) as Promise<ImageAnalysisRecord>;
  },

  /**
   * Update an existing Image Analysis record
   */
  async update(tableId: string, id: string, fields: Partial<ImageAnalysisRecord['fields']>): Promise<ImageAnalysisRecord> {
    return updateRecord(tableId, id, fields) as Promise<ImageAnalysisRecord>;
  },

  /**
   * Delete an Image Analysis record
   */
  async delete(tableId: string, id: string): Promise<{ id: string; deleted: boolean }> {
    return deleteRecord(tableId, id);
  },
};

/**
 * Company Table Module
 */
export const CompanyModule = {
  /**
   * Fetch all Company records
   */
  async getAll(tableId: string, options?: QueryOptions): Promise<CompanyRecord[]> {
    const response = await fetchRecords(tableId, options);
    return response.records as CompanyRecord[];
  },

  /**
   * Fetch a single Company record by ID
   */
  async getById(tableId: string, id: string): Promise<CompanyRecord | null> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `RECORD_ID() = '${id}'`,
      maxRecords: 1,
    });
    
    if (response.records.length === 0) {
      return null;
    }
    
    return response.records[0] as CompanyRecord;
  },

  /**
   * Fetch Company records by category
   */
  async getByCategory(tableId: string, category: string, options?: Omit<QueryOptions, 'filterByFormula'>): Promise<CompanyRecord[]> {
    const response = await fetchRecords(tableId, {
      ...options,
      filterByFormula: `FIND("${category.replace(/"/g, '\\"')}", ARRAYJOIN({Category}, ",")) > 0`,
    });
    
    return response.records as CompanyRecord[];
  },

  /**
   * Get companies by staining platform
   */
  async getByStainingPlatform(tableId: string, platformId: string): Promise<CompanyRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${platformId}", ARRAYJOIN({StainingPlatforms}, ",")) > 0`,
    });
    return response.records as CompanyRecord[];
  },

  /**
   * Get companies by image scanning method 
   */
  async getByImageScanning(tableId: string, scanningId: string): Promise<CompanyRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${scanningId}", ARRAYJOIN({ImageScanning}, ",")) > 0`,
    });
    return response.records as CompanyRecord[];
  },

  /**
   * Get companies by image analysis tool
   */
  async getByImageAnalysis(tableId: string, analysisId: string): Promise<CompanyRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${analysisId}", ARRAYJOIN({ImageAnalysis}, ",")) > 0`,
    });
    return response.records as CompanyRecord[];
  },

  /**
   * Get companies by location (partial match)
   */
  async getByLocation(tableId: string, location: string): Promise<CompanyRecord[]> {
    const response = await fetchRecords(tableId, {
      filterByFormula: `FIND("${location.replace(/"/g, '\\"')}", {Location}) > 0`,
    });
    return response.records as CompanyRecord[];
  },

  /**
   * Create a new Company record
   */
  async create(tableId: string, fields: Omit<CompanyRecord['fields'], 'id'>): Promise<CompanyRecord> {
    return createRecord(tableId, fields) as Promise<CompanyRecord>;
  },

  /**
   * Update an existing Company record
   */
  async update(tableId: string, id: string, fields: Partial<CompanyRecord['fields']>): Promise<CompanyRecord> {
    return updateRecord(tableId, id, fields) as Promise<CompanyRecord>;
  },

  /**
   * Delete a Company record
   */
  async delete(tableId: string, id: string): Promise<{ id: string; deleted: boolean }> {
    return deleteRecord(tableId, id);
  },
}; 
